import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  UserPasswordResetBody,
  UserPasswordsBody,
  AuthenticationProviderVarieties,
  UserPosition,
} from "@vviinn/user-managment-client-ts";
import {
  createUserWithEmailAndPassword,
  getAuth,
  IdTokenResult,
  signInWithEmailAndPassword,
  signInWithPopup,
  User,
  UserCredential,
  SAMLAuthProvider,
  OAuthProvider,
} from "firebase/auth";
import { ApiRequest, createApi, serializeModel } from "../../../api";
import { EmailAndPassword, Authentificate } from "../../../user/types";
import { FullRole } from "../../../user/role/types";
import { fetchCompanies } from "../companies/companies-slice";
import { Status } from "../types";

type UserStateProps = {
  credentials: UserCredential | null;
  claims: IdTokenResult | null;
  id: string | null;
  email: string | null;
  isAuth: boolean;
  onboarding?: boolean;
  emailVerified?: boolean;
  defaultCompanyRoleStatus: Status;
};

const initialState: UserStateProps = {
  credentials: null,
  claims: null,
  id: null,
  email: null,
  isAuth: false,
  onboarding: false,
  emailVerified: false,
  defaultCompanyRoleStatus: "idle",
};

// Best-effort: a viewer-role assignment failure shouldn't block sign-in.
// Only dispatch this for a verified email (or an SSO account, whose identity
// provider vouches for it) — the BFF trusts the token's email domain as-is,
// so an unverified address could claim someone else's domain.
// Runs at most once per session: the user slice isn't persisted and is reset
// on sign-out, and the condition skips a run that's in flight or succeeded.
// Reads the Firebase user directly rather than taking it as an argument, since
// a User instance in the action's meta would fail the serializability check.
export const assignDefaultCompanyRole = createAsyncThunk(
  "user/assignDefaultCompanyRole",
  async (_: void, { dispatch }) => {
    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error("No signed-in user");
      const api = createApi(await user.getIdToken(true));
      await api.assignDefaultCompanyRole();
      // The BFF just granted a company role via custom claims, but the ID token
      // above was minted before that — Firebase doesn't retroactively update
      // issued tokens. Refresh again so the new claim is actually usable (and
      // persisted, so it survives a reload), then refetch companies with it so
      // the new assignment shows up without requiring a manual page reload.
      const refreshedApi = createApi(await user.getIdToken(true));
      await dispatch(fetchCompanies(refreshedApi));
    } catch (error) {
      console.warn(error);
      throw error;
    }
  },
  {
    condition: (_, { getState }) => {
      const { defaultCompanyRoleStatus } = (
        getState() as { user: UserStateProps }
      ).user;
      return (
        defaultCompanyRoleStatus !== "loading" &&
        defaultCompanyRoleStatus !== "fulfilled"
      );
    },
  },
);

export const authentificate = createAsyncThunk(
  "user/authentificate",
  async (authData: Authentificate) => {
    const { email, password, providerId, providerType } = authData;

    const auth = getAuth();
    let authResponse: UserCredential;
    if (providerId) {
      const provider =
        providerType === "open_id_connect"
          ? new OAuthProvider(providerId)
          : new SAMLAuthProvider(providerId);
      authResponse = await signInWithPopup(auth, provider);
    } else if (email && password) {
      authResponse = await signInWithEmailAndPassword(auth, email, password);
    } else {
      throw new Error("No auth data provided");
    }

    return serializeModel<UserCredential>(authResponse);
  },
);

export const registrate = createAsyncThunk(
  "user/registrate",
  async (registerData: EmailAndPassword) => {
    const { email, password } = registerData;
    const auth = getAuth();
    const registerResponse = await createUserWithEmailAndPassword(
      auth,
      email,
      password,
    );
    return serializeModel<UserCredential>(registerResponse);
  },
);

export const resetPassword = createAsyncThunk(
  "user/password/reset",
  async ({ api, value }: ApiRequest<UserPasswordResetBody>) => {
    const { email, recaptchaToken } = value;
    await api.resetUserPassword({ email, recaptchaToken });
    return true;
  },
);

export const getAuthenticationProviders = createAsyncThunk(
  "products/fetch",
  async ({ api, value }: ApiRequest<UserPasswordResetBody>) => {
    const { email } = value;

    const response = await api.getAuthenticationProviders({ email });
    return serializeModel<AuthenticationProviderVarieties[]>(response.value);
  },
);

export type AssignGlobalRoleRequestBody = {
  role: FullRole;
  userId: User["uid"];
};

export const assignGlobalUserRole = createAsyncThunk(
  "user/assignUserGlobal",
  async ({ api, value }: ApiRequest<AssignGlobalRoleRequestBody>) => {
    const { userId, role } = value;
    await api.addUserRole(userId, { role: role });
    return true;
  },
);

export const unassignGlobalUserRole = createAsyncThunk(
  "user/unassignUserGlobal",
  async ({ api, value }: ApiRequest<AssignGlobalRoleRequestBody>) => {
    const { userId, role } = value;
    await api.removeUserRole(userId, { role: role });
    return true;
  },
);

export type ChangeUserPasswordRequest = {
  userId: User["uid"];
  userPasswordsBody: UserPasswordsBody;
};

export const changeUserPassword = createAsyncThunk(
  "user/changePassword",
  async ({ api, value }: ApiRequest<ChangeUserPasswordRequest>) => {
    const { userId, userPasswordsBody } = value;
    await api.changeUserPassword(userId, userPasswordsBody);
    return true;
  },
);

export type ChangeUserPositionRequest = {
  userId: User["uid"];
  position?: UserPosition["position"];
};
export const setUserPosition = createAsyncThunk(
  "user/setPosition",
  async ({ api, value }: ApiRequest<ChangeUserPositionRequest>) => {
    const { userId, position } = value;
    await api.setUserPosition(userId, { position: position ?? "" });
    return true;
  },
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    signIn(state, { payload }: PayloadAction<User>) {
      state.id = payload.uid;
      state.email = payload.email;
      state.emailVerified = payload.emailVerified;
    },

    signOut(state) {
      state = initialState;
    },

    setOnboarding(state, { payload }: PayloadAction<boolean>) {
      state.onboarding = payload;
    },

    setClaims(state, { payload }: PayloadAction<IdTokenResult>) {
      state.claims = serializeModel(payload);
      state.isAuth = true;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(assignDefaultCompanyRole.pending, (state) => {
      state.defaultCompanyRoleStatus = "loading";
    });
    builder.addCase(assignDefaultCompanyRole.fulfilled, (state) => {
      state.defaultCompanyRoleStatus = "fulfilled";
    });
    builder.addCase(assignDefaultCompanyRole.rejected, (state) => {
      state.defaultCompanyRoleStatus = "error";
    });
    builder.addCase(authentificate.fulfilled, (state, { payload }) => {
      state.credentials = payload;
    });
    builder.addCase(authentificate.rejected, (state) => {
      state.credentials = initialState.credentials;
    });
  },
});

export const { signIn, signOut, setClaims, setOnboarding } = userSlice.actions;
export default userSlice.reducer;
